import { createContext, type ReactNode, useContext, useMemo } from "react";

import { DEFAULT_LOCALE } from "./constants";
import { I18N_TRANSLATION_CATALOGS } from "./generated/translationCatalogs";
import { I18N_TRANSLATION_KEYS } from "./generated/translationKeys";
import { useTranslationSettings } from "./settings/useTranslationSettings";
import type { SupportedLocale, TranslationCatalogs, TranslationKeyMap } from "./types";

export interface I18nContextValue {
  catalogs: TranslationCatalogs;
  fallbackLocale: SupportedLocale;
  isEnabled: boolean;
  keyMap: TranslationKeyMap;
  locale: SupportedLocale;
}

interface I18nProviderProps {
  catalogs?: TranslationCatalogs;
  children: ReactNode;
  fallbackLocale?: SupportedLocale;
  isEnabled?: boolean;
  keyMap?: TranslationKeyMap;
  locale?: SupportedLocale;
}

const DEFAULT_CONTEXT_VALUE: I18nContextValue = {
  catalogs: I18N_TRANSLATION_CATALOGS,
  fallbackLocale: DEFAULT_LOCALE,
  isEnabled: false,
  keyMap: I18N_TRANSLATION_KEYS,
  locale: DEFAULT_LOCALE,
};

const I18nContext = createContext<I18nContextValue>(DEFAULT_CONTEXT_VALUE);

export function I18nProvider(props: I18nProviderProps) {
  const settings = useTranslationSettings();

  const value = useMemo<I18nContextValue>(
    () => ({
      catalogs: props.catalogs ?? I18N_TRANSLATION_CATALOGS,
      fallbackLocale: props.fallbackLocale ?? settings.settings.fallbackLocale,
      isEnabled: props.isEnabled ?? settings.settings.enabled,
      keyMap: props.keyMap ?? I18N_TRANSLATION_KEYS,
      locale: props.locale ?? settings.settings.locale,
    }),
    [
      props.catalogs,
      props.fallbackLocale,
      props.isEnabled,
      props.keyMap,
      props.locale,
      settings.settings.enabled,
      settings.settings.fallbackLocale,
      settings.settings.locale,
    ]
  );

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useI18nContext(): I18nContextValue {
  return useContext(I18nContext);
}
